import React, {useEffect, useMemo, useState} from "react";
import {LoggedInView} from "../common/LoggedInView";
import {Link, useLocation, useParams} from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {
    updateClientContact,
    resetUpdateClientContactStatus,
    getCurrentClientContact
} from "../../store/reducers/ClientContactsSlice";
import {RequestStatus} from "../../utils/Enums";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import ElogTextarea from "../common/form/ElogTextarea";
import CategoryType from "../../models/CategoryType";
import {apiClient} from "../../utils/ApiClient";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {getCurrentClient} from "../../store/reducers/ClientsSlice";
import {useFormik, Field, Form, FormikProvider} from "formik";
import {updateOfflineReason} from "../../store/reducers/OfflineReasonsSlice";
import {getCountries} from "../../store/reducers/CountriesSlice";

interface ModifyClientContactScreenProps {
}

export interface ModifyClientContactFormProps {
    name: string;
    plea: string;
    email: string;
    phone: string;
    comment?: string;
    comment2?: string;
    no_feedback: boolean;
    banned: boolean;
    groups: string[];
    removeLevel: string;
    backToAdvertising: boolean;
    apply_to_all: boolean;
}

export const ModifyClientContactScreen: React.FC<ModifyClientContactScreenProps> = () => {

    const dispatch = useAppDispatch();
    const location = useLocation();

    const {clientId, contactId} = useParams<{ clientId: string; contactId: string }>();

    const clientContactsState = useAppSelector((state) => state.clientContacts);
    const clientsState = useAppSelector((state) => state.clients);

    const requestStatus: RequestStatus = clientContactsState.updateClientContactStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Kliendid", url: "/clients"},
        {text: "Kliendikontaktid", url: `/clients/${clientId}/contacts`},
        {text: "Muuda kliendikontakti", url: `/clients/${clientId}/contacts/${contactId}/modify`},
    ];

    const languageOptions: Option[] = [
        {value: 'est', label: 'Eesti'},
        {value: 'eng', label: 'Inglise'},
        {value: 'fin', label: 'Soome'}
    ];

    const removalOptions: Option[] = [
        {value: '', label: 'Vali'},
        {value: '1', label: '1'},
        {value: '2', label: '2'},
        {value: '3', label: '3'},
    ];

    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);

    useEffect(() => {
        if (contactId && clientId) {
            dispatch(getCurrentClient({id: clientId}));
            dispatch(getCurrentClientContact({clientId: parseInt(clientId), contactId: parseInt(contactId)}));
        }
    }, [contactId]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success && clientId && contactId) {
            dispatch(getCurrentClientContact({clientId: parseInt(clientId), contactId: parseInt(contactId)}));
        }
    }, [requestStatus, clientId, contactId]);

    useEffect(() => {
        dispatch(resetUpdateClientContactStatus());
    }, [location]);

    useEffect(() => {
        apiClient.get("category-types/all-by-client/" + clientId).then((response) => {
            setCategoryTypes(response.data);
        });
    }, [clientId]);

    const selectedCategoryIds = useMemo(() => {
        return clientContactsState.currentClientContact?.categories?.map((category) => String(category.id));
    }, [clientContactsState.currentClientContact]);

    console.log('ContactInfo', selectedCategoryIds, clientContactsState.currentClientContact);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            name: clientContactsState?.currentClientContact?.name ?? "",
            plea: clientContactsState?.currentClientContact?.plea ?? "",
            emails: clientContactsState?.currentClientContact?.emails ?? [''],
            phone: clientContactsState?.currentClientContact?.phone ?? "",
            comment: clientContactsState?.currentClientContact?.comment ?? "",
            comment2: clientContactsState?.currentClientContact?.comment2 ?? "",
            language: clientContactsState?.currentClientContact?.language ?? "est",
            banned: clientContactsState?.currentClientContact?.banned_at ?? false,
            groups: selectedCategoryIds,
            noD: clientContactsState?.currentClientContact?.no_d == 1,
            removeLevel: '',
            backToAdvertising: false,
            apply_to_all: false,
        },
        onSubmit: (values: any) => {
            if (clientId && contactId) {
                dispatch(updateClientContact({
                    clientId: parseInt(clientId, 10),
                    contactId: parseInt(contactId, 10),
                    clientContactData: values
                }));
            }
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            <h1>Kliendi {clientsState?.currentClient?.name} kontaktisiku {clientContactsState?.currentClientContact?.name} muutmine</h1>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Kontakti andmed uuendatud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && clientContactsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {clientContactsState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>

                    <div className="form">
                        <h2>
                            <span>Kliendikontakti andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="name" mandatory={true}/>
                            <ElogInput label="Pöördumine" name="plea" mandatory={true}/>
                            {values.emails.map((email, index) => (
                                <>
                                    <ElogInput
                                        label={`E-mail ${index + 1}`}
                                        name={`emails.${index}`}
                                        type="email"
                                        mandatory={true}
                                        button={index > 0 && (
                                            <button
                                                style={{color: 'red'}}
                                                type="button"
                                                onClick={() => setFieldValue('emails', values.emails.filter((_, i) => i !== index))}
                                            >
                                                [X]
                                            </button>
                                        )}
                                    />
                                </>
                            ))}
                            <tr>
                                <th>
                                </th>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => setFieldValue('emails', [...values.emails, ''])}
                                    >
                                        Lisa
                                    </button>
                                </td>
                            </tr>
                            <ElogInput label="Telefon" name="phone" mandatory={true}/>
                            <ElogSelect label="Keel" name="language" options={languageOptions} mandatory={true}/>
                            <ElogTextarea label="Taustainfo" name="comment"/>
                            <ElogCheckbox label={'Eemalda D14 listist'} name={'noD'}/>
                            <ElogCheckbox label="Eemalda mailikampaaniatest" name="banned"/>
                            <ElogCheckbox label="Kõigile kontaktidele" name="apply_to_all"/>
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}

                    <div className="form">
                        <h2>
                            <span>Reklaamist eemaldamine</span>
                        </h2>
                        {clientContactsState?.currentClientContact?.removed_until_cycle && (
                            <p>Kontakt on eemaldatud kuni tsüklini {clientContactsState.currentClientContact.removed_until_cycle}</p>
                        )}
                        <table className="form">
                            <tbody>
                            <ElogSelect label="Valik" name="removeLevel" options={removalOptions} />
                            <ElogCheckbox label="Tagasi reklaami" name="backToAdvertising" />
                            </tbody>
                        </table>
                    </div>

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                        <strong>Muuda</strong>
                                    </span>
                            </button>
                            <Link to={`/clients/${clientId}/contacts`}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};